import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

/**
 * Visual vocabulary for the Fakturownia screens. Each tag is a tiny pill —
 * one colour per attribute, rendered in tables AND on the detail screen so
 * the finance manager learns to read documents at a glance.
 */

type DirectionValue = 'ISSUED' | 'RECEIVED';

interface IDirectionPillProps {
  direction?: string | null;
}

export const InvoiceDirectionPill = memo(
  ({ direction }: IDirectionPillProps) => {
    const { t } = useTranslation();
    if (!direction) return null;
    const upper = direction.toUpperCase() as DirectionValue;
    const isIssued = upper === 'ISSUED';
    return (
      <Pill variant={isIssued ? 'issued' : 'received'}>
        {isIssued
          ? t('pages.fakturownia.invoices.filters.direction.issued')
          : t('pages.fakturownia.invoices.filters.direction.received')}
      </Pill>
    );
  },
);

interface IKindPillProps {
  kind?: string | null;
  /** Phase 12: backend flag wins over `kind` string when present. */
  isCorrection?: boolean | null;
  isAdvance?: boolean | null;
  isFinal?: boolean | null;
  isEstimate?: boolean | null;
  isQuotation?: boolean | null;
}

/**
 * Phase 12: priority-based pill — correction beats everything else (it's the
 * most actionable signal), then final / advance / estimate / proforma, with
 * a fallback to «VAT». Falls back to legacy `kind === 'correction'` so old
 * call sites that only pass `kind` keep rendering correctly.
 */
export const InvoiceKindPill = memo(
  ({
    kind,
    isCorrection,
    isAdvance,
    isFinal,
    isEstimate,
    isQuotation,
  }: IKindPillProps) => {
    const { t } = useTranslation();
    if (isCorrection || kind === 'correction') {
      return (
        <Pill variant="correction">
          ↩ {t('pages.fakturownia.invoices.kindCorrection')}
        </Pill>
      );
    }
    if (isFinal) {
      return (
        <Pill variant="final">{t('pages.fakturownia.invoices.kindFinal')}</Pill>
      );
    }
    if (isAdvance) {
      return (
        <Pill variant="advance">
          {t('pages.fakturownia.invoices.kindAdvance')}
        </Pill>
      );
    }
    if (isEstimate) {
      return (
        <Pill variant="estimate">
          {t('pages.fakturownia.invoices.kindEstimate')}
        </Pill>
      );
    }
    // Proforma is the «other quotation» bucket — not estimate, but still
    // outside KPI accounting. Bare `isQuotation` after the estimate check
    // above means client_order / proforma.
    if (isQuotation) {
      return (
        <Pill variant="proforma">
          {t('pages.fakturownia.invoices.kindProforma')}
        </Pill>
      );
    }
    if (!kind) return null;
    return (
      <Pill variant="neutral">{t('pages.fakturownia.invoices.kindVat')}</Pill>
    );
  },
);

interface IStatusPillProps {
  status?: string | null;
}

/**
 * i18n key of a Fakturownia invoice status label — the one mapping behind
 * `InvoiceStatusPill`, shared with places that need the text without the
 * pill (a link tooltip on the student card, the group roster popover).
 */
export const invoiceStatusLabelKey = (status?: string | null): string => {
  const upper = (status || '').toUpperCase();
  const labelKey =
    upper === 'PAID'
      ? 'statusPaid'
      : upper === 'PARTIAL'
      ? 'statusPartial'
      : upper === 'REJECTED'
      ? 'statusRejected'
      : upper === 'SENT'
      ? 'statusSent'
      : 'statusIssued';
  return `pages.fakturownia.invoices.filters.${labelKey}`;
};

export const InvoiceStatusPill = memo(({ status }: IStatusPillProps) => {
  const { t } = useTranslation();
  if (!status) return null;
  const upper = status.toUpperCase();
  const variant: PillVariant =
    upper === 'PAID'
      ? 'paid'
      : upper === 'PARTIAL'
      ? 'partial'
      : upper === 'REJECTED'
      ? 'rejected'
      : 'neutral';
  return <Pill variant={variant}>{t(invoiceStatusLabelKey(status))}</Pill>;
});

interface IMatchStatusDotProps {
  matchStatus?: string | null;
}

/** Compact glanceable indicator — used in dense tables instead of a word. */
export const MatchStatusDot = memo(({ matchStatus }: IMatchStatusDotProps) => {
  const { t } = useTranslation();
  if (!matchStatus) return null;
  const upper = matchStatus.toUpperCase();
  if (upper === 'NOT_APPLICABLE') return null;

  const color =
    upper === 'UNMATCHED'
      ? 'var(--k-fg-f97316)'
      : upper === 'MANUAL'
      ? 'var(--k-fg-2563eb)'
      : 'var(--k-fg-22c55e)';
  const titleKey =
    upper === 'UNMATCHED'
      ? 'matchUnmatched'
      : upper === 'MANUAL'
      ? 'matchManual'
      : 'matchAuto';
  return (
    <Dot
      color={color}
      title={t(`pages.fakturownia.invoices.filters.${titleKey}`)}
    />
  );
});

interface IOverduePillProps {
  /** Days past due (positive integer). */
  days: number;
}

export const OverduePill = memo(({ days }: IOverduePillProps) => {
  const { t } = useTranslation();
  return (
    <Pill variant="overdue">
      {t('pages.fakturownia.invoices.overdue', { days })}
    </Pill>
  );
});

type PillVariant =
  | 'issued'
  | 'received'
  | 'correction'
  | 'paid'
  | 'partial'
  | 'rejected'
  | 'neutral'
  | 'overdue'
  | 'estimate'
  | 'advance'
  | 'final'
  | 'proforma';

const PILL_COLORS: Record<PillVariant, { bg: string; fg: string }> = {
  issued: { bg: 'var(--k-bg-dcfce7)', fg: 'var(--k-fg-166534)' },
  received: { bg: 'var(--k-bg-dbeafe)', fg: 'var(--k-fg-1e40af)' },
  correction: { bg: 'var(--k-bg-ffedd5)', fg: 'var(--k-fg-9a3412)' },
  paid: { bg: 'var(--k-bg-dcfce7)', fg: 'var(--k-fg-166534)' },
  partial: { bg: 'var(--k-bg-fef3c7)', fg: 'var(--k-fg-92400e)' },
  rejected: { bg: 'var(--k-bg-fee2e2)', fg: 'var(--k-fg-991b1b)' },
  neutral: { bg: 'var(--k-bg-f1f5f9)', fg: 'var(--k-fg-475569)' },
  overdue: { bg: 'var(--k-bg-fee2e2)', fg: 'var(--k-fg-991b1b)' },
  // Phase 12: chain document types — blue for the contract (estimate),
  // amber for partial payment (advance), green for closing (final),
  // muted for proforma since it's the «не в KPI» quotation cousin.
  estimate: { bg: 'var(--k-bg-dbeafe)', fg: 'var(--k-fg-1e40af)' },
  advance: { bg: 'var(--k-bg-fed7aa)', fg: 'var(--k-fg-9a3412)' },
  final: { bg: 'var(--k-bg-bbf7d0)', fg: 'var(--k-fg-166534)' },
  proforma: { bg: 'var(--k-bg-e2e8f0)', fg: 'var(--k-fg-64748b)' },
};

export const Pill = styled.span<{ variant: PillVariant }>`
  display: inline-block;
  padding: 2px 8px;
  font-size: 11px;
  font-weight: 600;
  border-radius: 999px;
  white-space: nowrap;
  ${({ variant }) => css`
    background: ${PILL_COLORS[variant].bg};
    color: ${PILL_COLORS[variant].fg};
  `}
`;

const Dot = styled.span<{ color: string }>`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${({ color }) => color};
  margin-right: 4px;
  vertical-align: middle;
`;
